import React, { useState } from 'react';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import times from 'lodash/times';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Wrapper } from './AssetBucketingEdit.styles';
import { StickyFooter } from '../../../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { useResetModuleWrapperStyles } from '../../../../_hooks/useResetModuleWrapperStyles';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { useDocumentTitle } from '../../../../_hooks/useDocumentTitle';
import { useEditAssetBucketingMutation } from './api/useEditAssetBucketingMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { TextLoading } from '../../../../CommonComponents/OPL/Text/Text.loading';
import { BucketLoading } from '../AssetBucketingResults/components/Bucket/Bucket.loading';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { ResetToDefaultModal } from './components/ResetToDefaultModal/ResetToDefaultModal';
import { ScrollHereOnce } from '../../../../CommonComponents/ScrollHereOnce';
import { AssetDragOverlay } from './components/DragOverlay/DragOverlay';
import { Buckets } from '../AssetBucketingResults/components/Buckets/Buckets';
import { BucketCountForm } from '../../../../AdviserSettingsOPL/tabs/AssetBucketing/components/PageLayoutForm/PageLayoutForm';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Textarea } from '../../../../CommonComponents/OPL/Textarea/Textarea';
import { moveAsset } from './utils/moveAsset';
import {
  AssetBucketingDetailsDTO,
  useAssetBucketingDetailsQuery,
} from './api/useAssetBucketingDetailsQuery';
import { parseBuckets } from './utils/parseBuckets';
import { useAssetBucketingColors } from '../AssetBucketingResults/hooks/useAssetBucketingColors';
import {
  EMPTY_BUCKET,
  FormValues,
  RemoveAssetFn,
  UNASSIGNED_BUCKET_INDEX,
  validation,
} from './AssetBucketingEdit.meta';
import { UnassignedAssets } from './components/UnassignedAssets/UnassignedAssets';
import { ChangeBucketCountModal } from './components/ChangeBucketCountModal/ChangeBucketCountModal';
import { removeBuckets } from './utils/removeBuckets';

interface Props {
  clientId: string;
}

export const AssetBucketingEdit = ({ clientId }: Props) => {
  useResetModuleWrapperStyles();

  useDocumentTitle('Edit Asset Bucketing');

  const { isFetching, isError, refetch, data } = useAssetBucketingDetailsQuery({ clientId });

  if (isFetching) {
    return (
      <>
        <ScrollHereOnce options={false} />

        <Wrapper as="main">
          <TextLoading width={200} height="h4" mb="24" />

          <Box as="section" display="flex" css="gap: 24px">
            {times(3).map(key => (
              <Box as="article" key={key} flex={1}>
                <BucketLoading />
              </Box>
            ))}
          </Box>
        </Wrapper>

        <StickyFooter />
      </>
    );
  }

  if (isError) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return <AssetBucketingEditContent clientId={clientId} data={data} />;
};

interface ContentProps {
  clientId: string;
  data: AssetBucketingDetailsDTO;
}

const AssetBucketingEditContent = ({ clientId, data }: ContentProps) => {
  const [isResetToDefaultModalOpen, setIsResetToDefaultModalOpen] = useState(false);

  const [newBucketCount, setNewBucketCount] = useState<number | null>(null);

  const {
    mutateAsync: editAssetBucketing,
    isLoading: isEditingAssetBucketing,
  } = useEditAssetBucketingMutation();

  const formBag = useForm<FormValues>({
    defaultValues: data,
    resolver: yupResolver(validation),
  });

  const bucketsFieldArray = useFieldArray({
    name: 'buckets',
    control: formBag.control,
  });
  const watchBuckets = formBag.watch('buckets');
  const buckets = bucketsFieldArray.fields.map((field, index) => {
    return {
      ...field,
      ...watchBuckets[index],
    };
  });
  const unassignedAssets = formBag.watch('unassignedAssets');

  const bucketColors = useAssetBucketingColors();

  const hasExcludedFromNetworth = data.buckets.some(({ assets }) =>
    assets.some(({ excludedFromNetworth }) => excludedFromNetworth),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { over, active } = event;

    if (over?.data.current && active.data.current) {
      const { bucketIndex: destinationBucketIndex } = over.data.current;

      const {
        data: {
          current: {
            bucketIndex: sourceBucketIndex,
            classification: { id: assetId },
          },
        },
      } = active;

      if (destinationBucketIndex !== sourceBucketIndex) {
        const result = moveAsset({
          assetId,
          sourceBucketIndex,
          destinationBucketIndex,
          buckets,
          unassignedAssets,
        });

        formBag.setValue('buckets', result.buckets);
        formBag.setValue('unassignedAssets', result.unassignedAssets);
      }
    }
  };

  const removeAsset: RemoveAssetFn = ({ assetId, bucketIndex }) => {
    const result = moveAsset({
      assetId,
      sourceBucketIndex: bucketIndex,
      destinationBucketIndex: UNASSIGNED_BUCKET_INDEX,
      buckets,
      unassignedAssets,
    });

    formBag.setValue('buckets', result.buckets);
    formBag.setValue('unassignedAssets', result.unassignedAssets);
  };

  const changeBucketCount = (bucketCount: number) => {
    formBag.setValue('headline', '');

    // Reducing the number of buckets
    if (bucketCount < buckets.length) {
      const result = removeBuckets({
        bucketsToRemoveCount: buckets.length - bucketCount,
        buckets,
        unassignedAssets,
      });

      formBag.setValue('buckets', result.buckets);
      formBag.setValue('unassignedAssets', result.unassignedAssets);
    }

    // Increasing the number of buckets
    if (bucketCount > buckets.length) {
      times(bucketCount - buckets.length).forEach(() => {
        bucketsFieldArray.append(EMPTY_BUCKET);
      });
    }
  };

  const handleGoBack = () => {
    ADV.util.Router.go2('AssetBucketingResults', { clientId, year: new Date().getFullYear() });
  };

  const onSubmit = formBag.handleSubmit(async values => {
    try {
      await editAssetBucketing({ clientId, ...values });

      toast.success('Changes were successfully saved');

      handleGoBack();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }

      console.error(error);
    }
  });

  return (
    <FormProvider {...formBag}>
      <Box css="overflow-x: auto">
        <Wrapper
          as="form"
          onSubmit={onSubmit}
          aria-label="Edit asset bucketing"
          id="form--edit-asset-bucketing"
          css="min-width: 1200px"
        >
          <Box
            as="header"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb="32"
          >
            <Text as="h1" fontSize="h4" fontWeight={600}>
              Edit Asset Bucketing
            </Text>

            <Button onClick={() => setIsResetToDefaultModalOpen(true)} variant="link" ml="8">
              Reset to Default
            </Button>
          </Box>

          <BucketCountForm bucketCount={buckets.length} onChange={setNewBucketCount} />

          <Box mt="32" mb="24">
            <Input
              {...formBag.register('headline')}
              label="Headline"
              type="text"
              maxLength={40}
              css="max-width: 300px"
              mb="16"
              formVariant={formBag.formState.errors?.headline ? 'error' : undefined}
              helperText={formBag.formState.errors?.headline?.message || 'Max. 40 characters'}
            />

            <Textarea
              {...formBag.register('supportingText')}
              label="Supporting Text"
              rows={6}
              maxLength={250}
              formVariant={formBag.formState.errors?.supportingText ? 'error' : undefined}
              helperText={
                formBag.formState.errors?.supportingText?.message || 'Max. 250 characters'
              }
            />
          </Box>

          <DndContext onDragEnd={handleDragEnd}>
            <Buckets
              buckets={parseBuckets({ buckets, bucketColors })}
              isEditable
              onRemoveAsset={removeAsset}
            />

            {hasExcludedFromNetworth && (
              <Text fontSize="small" py="8">
                *Non balance sheet items will not contribute to Net Worth.
              </Text>
            )}

            <UnassignedAssets assets={unassignedAssets} />

            <AssetDragOverlay />
          </DndContext>
        </Wrapper>
      </Box>

      <StickyFooter mt={0}>
        <Button
          onClick={handleGoBack}
          variant="secondary"
          size="large"
          leftIcon="fas fa-chevron-left"
          mr="auto"
        >
          Back
        </Button>

        <Button
          type="submit"
          size="large"
          isLoading={isEditingAssetBucketing}
          form="form--edit-asset-bucketing"
        >
          Save
        </Button>
      </StickyFooter>

      {isResetToDefaultModalOpen && (
        <ResetToDefaultModal
          clientId={clientId}
          onClose={() => {
            setIsResetToDefaultModalOpen(false);
            handleGoBack();
          }}
        />
      )}

      {newBucketCount !== null && (
        <ChangeBucketCountModal
          onChange={() => changeBucketCount(newBucketCount)}
          onClose={() => setNewBucketCount(null)}
        />
      )}
    </FormProvider>
  );
};
